import { decode } from 'base64-arraybuffer';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';

import { supabase } from '@/lib/supabase';

async function resizeAndCompress(uri: string, maxSize: number, quality: number): Promise<{ base64: string; contentType: string }> {
  const result = await ImageManipulator.manipulateAsync(
    uri,
    [{ resize: { width: maxSize, height: maxSize } }],
    { compress: quality, format: ImageManipulator.SaveFormat.JPEG, base64: true },
  );
  return { base64: result.base64!, contentType: 'image/jpeg' };
}

async function deleteExistingFiles(userId: string, prefix: string) {
  const { data } = await supabase.storage.from('avatars').list(userId, { search: prefix });
  if (data && data.length > 0) {
    await supabase.storage.from('avatars').remove(data.map((f) => `${userId}/${f.name}`));
  }
}

export async function pickAndUploadAvatar(userId: string): Promise<string | null> {
  // Χωρίς requestMediaLibraryPermissionsAsync() — το launchImageLibraryAsync
  // ανοίγει το system Photo Picker του Android, που δεν χρειάζεται καθόλου
  // άδεια (Google Play policy: broad READ_MEDIA_IMAGES/VIDEO permissions
  // απαγορεύονται όταν αρκεί ο system picker).
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    allowsEditing: true,
    aspect: [1, 1],
    quality: 1,
  });

  const asset = result.assets?.[0];
  if (result.canceled || !asset?.uri) return null;

  const { base64, contentType } = await resizeAndCompress(asset.uri, 256, 0.75);
  const path = `${userId}/avatar.jpg`;

  await deleteExistingFiles(userId, 'avatar');

  const { error: upErr } = await supabase.storage
    .from('avatars')
    .upload(path, decode(base64), { contentType, cacheControl: '3600', upsert: true });
  if (upErr) throw upErr;

  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  // Το path είναι πάντα το ίδιο (avatar.jpg) — χωρίς cache-busting query param
  // το URL στη βάση δεν αλλάζει ποτέ σε νέο upload, οπότε ούτε η εφαρμογή
  // ανιχνεύει αλλαγή ούτε το expo-image disk cache φέρνει τα νέα bytes.
  const cacheBustedUrl = `${data.publicUrl}?v=${Date.now()}`;

  const { error: updErr } = await supabase
    .from('profiles')
    .update({ avatar_url: cacheBustedUrl })
    .eq('id', userId);
  if (updErr) throw updErr;

  return cacheBustedUrl;
}

export function effectiveAvatarUrl(profile: {
  avatar_url?: string | null;
  use_logo_as_avatar?: boolean | null;
  business_logo_url?: string | null;
} | null | undefined): string | null | undefined {
  if (profile?.use_logo_as_avatar && profile?.business_logo_url) {
    return profile.business_logo_url;
  }
  return profile?.avatar_url;
}

export async function pickAndUploadLogo(userId: string): Promise<string | null> {
  // Χωρίς requestMediaLibraryPermissionsAsync() — το launchImageLibraryAsync
  // ανοίγει το system Photo Picker του Android, που δεν χρειάζεται καθόλου
  // άδεια (Google Play policy: broad READ_MEDIA_IMAGES/VIDEO permissions
  // απαγορεύονται όταν αρκεί ο system picker).
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    allowsEditing: true,
    aspect: [1, 1],
    quality: 1,
  });

  const asset = result.assets?.[0];
  if (result.canceled || !asset?.uri) return null;

  const { base64, contentType } = await resizeAndCompress(asset.uri, 256, 0.75);
  const path = `${userId}/logo.jpg`;

  await deleteExistingFiles(userId, 'logo');

  const { error: upErr } = await supabase.storage
    .from('avatars')
    .upload(path, decode(base64), { contentType, cacheControl: '3600', upsert: true });
  if (upErr) throw upErr;

  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  const cacheBustedUrl = `${data.publicUrl}?v=${Date.now()}`;

  const { error: updErr } = await supabase
    .from('profiles')
    .update({ business_logo_url: cacheBustedUrl })
    .eq('id', userId);
  if (updErr) throw updErr;

  return cacheBustedUrl;
}
